<template>
    <Head title="La Boutique" />

    <TravelLayout :currency="currency">
        <div x-data="handleSelect">
            <Datatables backgroundClass="bg-primaryOrange" textClass="text-gray-800">
                <template #title>
                    Liste des articles de la boutique
                    <span class="font-medium">({{ items.total }})</span>
                </template>
                <template #headerButtons>
                    <div class="grid grid-flow-col sm:auto-cols-max sm:justify-end gap-2">
                        <button @click="showSearchModal" title="Rechercher"
                            class="rounded-md py-1 px-2 bg-white text-primaryOrange hover:bg-primaryOrange-300 hover:text-white"
                        ><i  class="fad fa-search"></i>
                        </button>
                        <button v-if="$page.props.permissions.canAddItem"
                            @click.prevent="emitter.emit('showItemModal')"
                            class="btn bg-white text-primaryOrange hover:bg-primaryOrange-300 hover:text-white py-1 px-2"
                        >
                            <svg class="h-4 w-4 fill-current shrink-0" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clip-rule="evenodd" />
                            </svg>
                            <span class="text-xs ml-1">Ajouter un article</span>
                        </button>
                    </div>
                </template>
                <template #tableHeader>
                    <th>#</th>
                    <th>Référence</th>
                    <th>Nom</th>
                    <th>Prix</th>
                    <th>Client</th>
                    <th><i class="fab fa-whatsapp text-green-500 text-sm"></i></th>
                    <th>Statut</th>
                    <th class="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <span class="sr-only">Menu</span>
                    </th>
                </template>
                <template #tableBody>
                    <template v-if="!items.data.length">
                        <tr>
                            <td colspan="8">
                                <div class="text-center text-2xl text-red-400 bg-red-100 p-10">
                                    Aucun article enregistré pour le moment.
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else v-for="(item, index) in items.data" :key="item.id">
                        <tr>
                            <td>{{ index + 1 }}</td>
                            <td>
                                <span class="font-bold text-primaryOrange">{{ item.reference }}</span>
                            </td>
                            <td>{{ item.short_name_table }}</td>
                            <td>
                                <div class="text-right font-bold"
                                    :class="item.balance > 0 ? 'text-gray-400' : 'text-primaryOrange'"
                                >{{ formatAmount(item.price, currency) }}</div>
                            </td>
                            <td>
                                <div v-if="item.customer" class="flex items-center">
                                    <div class="shrink-0 flex items-center justify-center bg-gray-700 rounded-full mr-2">
                                        <img class="w-10 h-10 rounded-full border-2 border-white box-content"
                                            :src="item.customer.profile_photo_url"
                                        />
                                    </div>
                                    <div class="grow text-left">
                                        <h2 class="font-semibold text-gray-800 inline-flex">{{ item.customer.name }}</h2>
                                        <div class="text-xs text-gray-500 font-semibold">{{ item.customer.full_phone }}</div>
                                    </div>
                                </div>
                                <span v-else class="text-yellow-500 italic font-semibold">Article non vendu</span>
                            </td>
                            <td>
                                <b :class="item.whatsapp_clicks > 0 ? 'text-green-500' : 'text-gray-400'">{{ item.whatsapp_clicks }}</b>
                            </td>
                            <td><StatusBadge :label="item.status_text" :color="item.status_color" /></td>
                            <td class="whitespace-nowrap w-px">
                                <div class="space-x-5 flex items-center text-right">
                                    <i @click.prevent="openDetails(item)" aria-controls="description-01"
                                        class="fad fa-arrow-down text-sm text-sky hover:text-sky-400 transform
                                        cursor-pointer transition-transform duration-200 ease-in-out"
                                        :class="{ 'rotate-180' : selectedItem && selectedItem.id === item.id }"
                                        :title="selectedItem && selectedItem.id === item.id ? 'Cacher' : 'Détails'"
                                    ></i>

                                    <i v-if="!item.payments.length && $page.props.permissions.canUpdateItem"
                                        @click.prevent="emitter.emit('showItemModal', item)"
                                        class="fad fa-pencil text-sm text-sky-500 hover:text-sky-400 cursor-pointer"
                                        title="Modifier"
                                    ></i>
                                    <i v-else class="fad fa-pencil text-sm text-gray-500 opacity-50 cursor-not-allowed"
                                        title="Impossible de modifier"
                                    ></i>

                                    <i v-if="!item.payments.length && $page.props.permissions.canDeleteItem"
                                        @click.prevent="destroyItem(item)"
                                        class="fad fa-trash text-sm text-red-500 hover:text-red-400 cursor-pointer"
                                        title="Supprimer"
                                    ></i>
                                    <i v-else class="fad fa-trash text-sm text-gray-500 opacity-50 cursor-not-allowed"
                                        title="Impossible de supprimer"
                                    ></i>

                                    <i v-if="!item.payments.length && $page.props.permissions.canBeMigrateItem"
                                        @click.prevent="emitter.emit('showMigrateItemModal', item)"
                                        class="fad fa-exchange text-sm text-violet hover:text-violet-dark cursor-pointer"
                                        title="Transférer"
                                    ></i>
                                    <i v-else class="fad fa-exchange text-sm text-gray-500 opacity-50 cursor-not-allowed"
                                        title="Impossible de transférer"
                                    ></i>
                                </div>
                            </td>
                        </tr>
                        <tr id="description-01" role="region" v-show="selectedItem && selectedItem.id === item.id">
                            <td colspan="8">
                                <ItemDetails :item="item" :modes="modes" :categories="categories" :currency="currency" :travel="travel" />
                            </td>
                        </tr>
                    </template>
                </template>
            </Datatables>
        </div>

        <Pagination :links="items.links" />

        <ItemForm
            v-if="$page.props.permissions.canUpdateItem || $page.props.permissions.canAddItem"
            :modes="modes"
            :travel="travel"
            :currency="currency"
            :categories="categories"
        />

        <MigrateItemForm
            v-if="$page.props.permissions.canBeMigrateItem"
            :travel="travel"
        />

        <JetDialogModal :show="searchModal" @close="closeSearchModal" max-width="xl" backgroundClass="bg-primaryOrange">
            <template #title>Rechercher un article</template>

            <template #content>
                <GlobalFilter @search-launched="closeSearchModal" />
            </template>
        </JetDialogModal>
    </TravelLayout>
</template>

<script>
    import { defineComponent } from 'vue'
    import useDevice from '@/composables/device'
    import ItemForm from '@/Components/ItemForm'
    import { Head } from '@inertiajs/inertia-vue3';
    import useDropdown from '@/composables/dropDown'
    import Pagination from '@/Components/Pagination'
    import Datatables from '@/Components/Datatables'
    import StatusBadge from '@/Components/StatusBadge'
    import PrintMixins from '@/Mixins/PrintMixins.vue'
    import ItemDetails from '@/Components/ItemDetails'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import GlobalFilter from '@/Components/GlobalFilter'
    import CurrencyMixins from '@/Mixins/CurrencyMixins'
    import TravelLayout from '@/Layouts/TravelLayout.vue'
    import MigrateItemForm from '@/Components/MigrateItemForm'
    import SearchActionMixins from '@/Mixins/SearchActionMixins'

    export default defineComponent({
        components: {
            Head,
            ItemForm,
            Pagination,
            Datatables,
            StatusBadge,
            ItemDetails,
            TravelLayout,
            GlobalFilter,
            JetDialogModal,
            MigrateItemForm
        },

        mixins: [PrintMixins, CurrencyMixins, SearchActionMixins],

        props: {
            modes: Array,
            items: Object,
            travel: Object,
            currency: Object,
            categories: Array
        },

        setup() {
            const { isMobile, isTablet, isDesktop } = useDevice()

            const { dropdownOpen, trigger, dropdown } = useDropdown()

            return {
                isMobile,
                isTablet,
                isDesktop,

                dropdownOpen,
                trigger,
                dropdown
            }
        },

        data() {
            return {
                selectedItem: null,
                manageModal: false
            }
        },

        methods: {
            openDetails(item) {
                if (this.selectedItem && this.selectedItem.id === item.id) {
                    this.selectedItem = null;
                } else {
                    this.selectedItem = item
                }
            },

            destroyItem(item) {
                this.$swal.queue([{
                    title: "Article : " + item.reference,
                    text: "Etes vous sûr de vouloir supprimer l'article ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: 'rgb(241, 162, 0)',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Supprimer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.$inertia.delete(route('travels.items.destroy', {travel: this.travel, 'item': item}), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire(
                                    'Article',
                                    'Article supprimé avec succès.',
                                    'success'
                                )
                            },
                            onError: (error) => {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: error.message
                                })
                            }
                        })
                    }
                }])
            }
        }
    })
</script>
